"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Star, User } from "lucide-react";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { SQUARE_SM, imagePresetUrl } from "@/lib/images/presets";
import { UnifiedFollowButton } from "@/components/UnifiedFollowButton";
import { ProfileStatInline } from "@/components/Profile/profile-stat-inline";
import { useSuggestedSellers } from "@/hooks/use-suggested-sellers";
import type { ProfileSuggestedUser } from "@kraahl/contract";

interface FollowSuggestionsCarouselProps {
  currentUserId: string | null;
  excludeProfileId?: string;
  onFollowChange?: (userId: string) => void;
  title?: string;
  className?: string;
  /** Foil the Follow CTA (feed discovery contexts). Off on profile pages. */
  foilFollow?: boolean;
}

export function FollowSuggestionsCarousel({
  currentUserId,
  excludeProfileId,
  onFollowChange,
  title = "Suggested for you",
  className,
  foilFollow = false,
}: FollowSuggestionsCarouselProps) {
  const state = useSuggestedSellers({ excludeProfileId, limit: 12 });

  const handleFollow = (userId: string) => {
    onFollowChange?.(userId);
  };

  // Nothing to show and nothing to promise: the carousel renders no skeleton,
  // so `loading` and `error` collapse into the same "not there yet" absence.
  if (state.kind !== "ready" || state.sellers.length === 0) return null;

  return (
    <div className={className ?? "mt-6"}>
      <h3 className="text-sm font-semibold text-foreground mb-3">{title}</h3>
      <div
        className="flex gap-4 overflow-x-auto pb-2 pl-1 scrollbar-hide"
        style={{ scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}
      >
        {state.sellers.map((u) => (
          <SuggestionCard
            key={u.id}
            user={u}
            currentUserId={currentUserId}
            onFollowChange={handleFollow}
            foil={foilFollow}
          />
        ))}
      </div>
    </div>
  );
}

function SuggestionCard({
  user,
  currentUserId,
  onFollowChange,
  foil = false,
}: {
  user: ProfileSuggestedUser;
  currentUserId: string | null;
  onFollowChange?: (userId: string) => void;
  foil?: boolean;
}) {
  const avatarUrl = getAvatarUrl(user.avatar_url);
  const [following, setFollowing] = useState(user.is_following);

  const handleFollowChange = (next: boolean) => {
    setFollowing(next);
    if (next) onFollowChange?.(user.id);
  };

  return (
    <Link
      href={`/${user.username}`}
      className="shrink-0 w-[140px] flex flex-col items-center rounded-3xl overflow-hidden bg-card border border-border/60 hover:bg-muted/50 transition-colors scroll-snap-start p-4"
      style={{ scrollSnapAlign: "start" }}
    >
      <div className="w-16 h-16 rounded-full overflow-hidden bg-muted flex items-center justify-center shrink-0">
        {avatarUrl ? (
          <Image
            // `w-16 h-16` = 64 CSS px -> SQUARE_SM. Sized at the render site:
            // the variable also feeds `UnifiedFollowButton`.
            src={imagePresetUrl(avatarUrl, SQUARE_SM)}
            alt=""
            width={64}
            height={64}
            className="w-full h-full object-cover"
          />
        ) : (
          <User className="h-8 w-8 text-muted-foreground" />
        )}
      </div>
      <span className="text-sm font-medium text-foreground truncate w-full text-center mt-2">
        @{user.username}
      </span>
      <SuggestionCardMeta user={user} />
      <div
        className="mt-2 w-full flex justify-center"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <UnifiedFollowButton
          currentUserId={currentUserId}
          profileId={user.id}
          initialFollowing={following}
          variant="compact"
          context="carousel"
          profileUsername={user.username}
          profileAvatarUrl={avatarUrl}
          promptNotificationsOnFollow={false}
          onFollowChange={handleFollowChange}
          foil={foil}
          className="text-xs"
        />
      </div>
    </Link>
  );
}

/**
 * Meta line under the card name — "[bag] sales · [★] rating" (icon + number, no
 * text label), mirroring the mobile app's canonical ProfileDiscoveryCard. Sales
 * is the primary stat (listings only when sales is absent); rating follows.
 * Old payloads without the additive stats degrade to the follower count.
 */
function SuggestionCardMeta({ user }: { user: ProfileSuggestedUser }) {
  const rating = user.rating != null ? user.rating.toFixed(1) : null;
  const hasPrimary = user.sales_count != null || user.listings_count != null;

  if (!hasPrimary && rating == null) {
    return (
      <div className="mt-1 flex items-center justify-center">
        <ProfileStatInline
          stat="followers"
          value={user.followers_count}
          iconClassName="size-3 text-muted-foreground"
          valueClassName="text-[12px] font-medium text-muted-foreground"
        />
      </div>
    );
  }

  return (
    <div className="mt-1 flex items-center justify-center gap-1.5 text-[12px] text-muted-foreground">
      {user.sales_count != null ? (
        <ProfileStatInline
          stat="sales"
          value={user.sales_count}
          iconClassName="size-3 text-muted-foreground"
          valueClassName="text-[12px] font-medium text-muted-foreground"
        />
      ) : user.listings_count != null ? (
        <ProfileStatInline
          stat="listings"
          value={user.listings_count}
          iconClassName="size-3 text-muted-foreground"
          valueClassName="text-[12px] font-medium text-muted-foreground"
        />
      ) : null}
      {hasPrimary && rating != null ? <span className="text-foreground/30">·</span> : null}
      {rating != null ? (
        <span className="inline-flex items-center gap-0.5">
          <Star className="size-3 fill-amber-400 text-amber-400" />
          <span className="font-medium tabular-nums text-muted-foreground">{rating}</span>
        </span>
      ) : null}
    </div>
  );
}
